import React, { useState } from 'react';
import { UserPrivilege } from '../../types/index.js';
import { useDialog } from '../ui/DialogProvider.js';
import { useDataTable } from '../../hooks/useDataTable.js';
import { Pagination } from '../ui/Pagination.js';
import { SortableHeader } from '../ui/SortableHeader.js';
import { Plus, Search, ShieldCheck, AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';

interface UserPrivilegeViewProps {
  userPrivilegeList: UserPrivilege[];
  onAddPrivilege: (data: Partial<UserPrivilege>) => Promise<void>;
  onUpdatePrivilege: (id: string, data: Partial<UserPrivilege>) => Promise<void>;
  onDeletePrivilege: (id: string) => Promise<void>;
}

export const UserPrivilegeView: React.FC<UserPrivilegeViewProps> = ({
  userPrivilegeList,
  onAddPrivilege,
  onUpdatePrivilege,
  onDeletePrivilege,
}) => {
  const { showConfirm, showAlert } = useDialog();
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState<UserPrivilege | null>(null);

  // Form State
  const [username, setUsername] = useState('');
  const [nama, setNama] = useState('');
  const [levelUser, setLevelUser] = useState<UserPrivilege['levelUser']>('KASIR_LOKET');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<'Aktif' | 'Nonaktif'>('Aktif');
  const [canTransaksi, setCanTransaksi] = useState(true);
  const [canVoid, setCanVoid] = useState(false);
  const [canSetting, setCanSetting] = useState(false);
  const [canReport, setCanReport] = useState(true);

  // UX Feedback State
  const [errorMsg, setErrorMsg] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    searchQuery: search,
    setSearchQuery: setSearch,
    sortBy,
    sortDir,
    handleSort,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    totalPages,
    totalRows,
    paginatedData: filteredPrivileges,
  } = useDataTable<UserPrivilege>({
    data: userPrivilegeList,
    searchFields: ['username', 'nama', 'levelUser'],
    initialSortBy: 'nama',
    initialSortDir: 'asc',
  });

  const handleOpenAdd = () => {
    setEditItem(null);
    setUsername('');
    setNama('');
    setLevelUser('KASIR_LOKET');
    setPassword('');
    setStatus('Aktif');
    setCanTransaksi(true);
    setCanVoid(false);
    setCanSetting(false);
    setCanReport(true);
    setErrorMsg('');
    setShowPassword(false);
    setIsSubmitting(false);
    setShowModal(true);
  };

  const handleOpenEdit = (user: UserPrivilege) => {
    setEditItem(user);
    setUsername(user.username);
    setNama(user.nama);
    setLevelUser(user.levelUser);
    setPassword(''); // biarkan kosong jika tidak ingin ganti password
    setStatus(user.status);
    setCanTransaksi(user.canTransaksi);
    setCanVoid(user.canVoid);
    setCanSetting(user.canSetting);
    setCanReport(user.canReport);
    setErrorMsg('');
    setShowPassword(false);
    setIsSubmitting(false);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanUsername = username.trim();
    const cleanNama = nama.trim();

    if (!cleanUsername) {
      setErrorMsg('Username wajib diisi!');
      return;
    }
    if (!cleanNama) {
      setErrorMsg('Nama lengkap wajib diisi!');
      return;
    }

    if (!editItem) {
      if (!password || password.trim().length < 6) {
        setErrorMsg('Password wajib diisi minimal 6 karakter demi keamanan akun!');
        return;
      }
    } else {
      if (password && password.trim().length < 6) {
        setErrorMsg('Password baru minimal 6 karakter jika ingin diubah!');
        return;
      }
    }

    const data = { 
      username: cleanUsername, 
      nama: cleanNama, 
      levelUser, 
      password: password.trim() ? password.trim() : undefined, 
      status, 
      canTransaksi, 
      canVoid, 
      canSetting, 
      canReport 
    };
    
    try {
      setIsSubmitting(true);
      setErrorMsg('');
      if (editItem) {
        await onUpdatePrivilege(editItem.id, data);
        showAlert('Data hak akses pengguna berhasil diperbarui.', { type: 'success', title: 'Berhasil' });
      } else {
        await onAddPrivilege(data);
        showAlert('Pengguna baru berhasil ditambahkan.', { type: 'success', title: 'Berhasil' });
      }
      setShowModal(false);
    } catch (err: any) {
      const msg = err.message || 'Gagal menyimpan data pengguna';
      setErrorMsg(msg);
      showAlert(msg, { type: 'danger', title: 'Gagal Menyimpan' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string, nama: string) => {
    if (await showConfirm(`Hapus akses untuk pengguna ${nama}?`, { title: 'Hapus User Privilege', type: 'danger' })) {
      await onDeletePrivilege(id);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-xs animate-in fade-in duration-200 space-y-4">
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-6 h-6 text-sky-600" />
          <h3 className="font-bold text-sm text-slate-900">Hak Akses Pengguna (Role-Based Access Control)</h3>
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-64">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Cari pengguna..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 transition-all"
            />
          </div>
          <button
            onClick={handleOpenAdd}
            className="flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Tambah Pengguna</span>
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full text-left text-sm whitespace-nowrap">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="py-3 px-4 font-semibold text-slate-600 w-16 text-center">No</th>
              <SortableHeader label="Username" columnKey="username" currentSortBy={sortBy} currentSortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="Nama" columnKey="nama" currentSortBy={sortBy} currentSortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="Level User" columnKey="levelUser" currentSortBy={sortBy} currentSortDir={sortDir} onSort={handleSort} />
              <th className="py-3 px-4 font-semibold text-slate-600">Status & Akses</th>
              <th className="py-3 px-4 font-semibold text-slate-600 text-center w-24">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredPrivileges.map((item, idx) => (
              <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                <td className="py-3 px-4 text-center text-slate-500 font-mono text-xs">{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                <td className="py-3 px-4 font-medium text-slate-800">{item.username}</td>
                <td className="py-3 px-4 text-slate-600">{item.nama}</td>
                <td className="py-3 px-4">
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-bold border ${
                      item.levelUser === 'SUPERADMIN' || item.levelUser === 'ADMIN_KEUANGAN'
                        ? 'bg-sky-100 text-sky-800 border-sky-200'
                        : item.levelUser === 'KASIR_LOKET'
                        ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                        : item.levelUser === 'KEPALA_SEKOLAH'
                        ? 'bg-amber-100 text-amber-800 border-amber-200'
                        : 'bg-slate-100 text-slate-700 border-slate-200'
                    }`}
                  >
                    {item.levelUser.replace('_', ' ')}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <div className="flex flex-col gap-1 text-[10px]">
                    <div>
                      <span className={`px-1.5 py-0.5 rounded-full font-bold ${item.status === 'Aktif' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                        {item.status}
                      </span>
                    </div>
                    <div className="flex gap-1 text-slate-500 font-semibold mt-1">
                      {item.canTransaksi && <span className="bg-slate-100 border px-1 rounded" title="Bisa Transaksi">TRX</span>}
                      {item.canVoid && <span className="bg-slate-100 border px-1 rounded" title="Bisa Void">VOID</span>}
                      {item.canSetting && <span className="bg-slate-100 border px-1 rounded" title="Bisa Setting">SET</span>}
                      {item.canReport && <span className="bg-slate-100 border px-1 rounded" title="Akses Laporan">RPT</span>}
                    </div>
                  </div>
                </td>
                <td className="py-3 px-4 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={() => handleOpenEdit(item)}
                      className="text-sky-600 hover:text-sky-800 text-xs font-semibold px-2 py-1 rounded hover:bg-sky-50 transition"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item.id, item.nama)}
                      className="text-rose-600 hover:text-rose-800 text-xs font-semibold px-2 py-1 rounded hover:bg-rose-50 transition"
                    >
                      Hapus
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredPrivileges.length === 0 && (
              <tr>
                <td colSpan={6} className="py-8 text-center text-slate-400 text-sm">
                  Tidak ada data pengguna ditemukan.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalRows={totalRows}
        rowsPerPage={rowsPerPage}
        onPageChange={setCurrentPage}
        onRowsPerPageChange={setRowsPerPage}
      />

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden border border-slate-200">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-bold text-slate-800">{editItem ? 'Edit Pengguna' : 'Tambah Pengguna Baru'}</h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 transition">
                &times;
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4 text-sm">
              {errorMsg && (
                <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg flex items-start gap-2 text-rose-700 text-xs animate-in fade-in">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-rose-600" />
                  <span className="font-medium leading-relaxed">{errorMsg}</span>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Username</label>
                <input
                  required
                  type="text"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    if (errorMsg) setErrorMsg('');
                  }}
                  className="w-full px-3 py-2 border rounded-lg bg-slate-50 focus:bg-white transition"
                  placeholder="Contoh: admin_tu"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Nama Lengkap</label>
                <input
                  required
                  type="text"
                  value={nama}
                  onChange={(e) => {
                    setNama(e.target.value);
                    if (errorMsg) setErrorMsg('');
                  }}
                  className="w-full px-3 py-2 border rounded-lg bg-slate-50 focus:bg-white transition"
                  placeholder="Contoh: Budi Santoso"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Password {editItem ? '(Kosongkan jika tidak diubah)' : '(Wajib diisi)'}
                </label>
                <div className="relative">
                  <input
                    required={!editItem}
                    minLength={editItem && !password ? undefined : 6}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (errorMsg) setErrorMsg('');
                    }}
                    className="w-full pl-3 pr-10 py-2 border rounded-lg bg-slate-50 focus:bg-white transition text-sm"
                    placeholder={editItem ? 'Kosongkan jika tetap' : 'Minimal 6 karakter'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  {editItem
                    ? 'Kosongkan jika tidak ingin mengubah password (minimal 6 karakter jika diganti).'
                    : 'Minimal 6 karakter demi keamanan otentikasi akun.'}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Level User</label>
                  <select
                    value={levelUser}
                    onChange={(e) => setLevelUser(e.target.value as any)}
                    className="w-full px-3 py-2 border rounded-lg bg-slate-50 focus:bg-white transition font-medium text-xs"
                  >
                    <option value="SUPERADMIN">Super Admin</option>
                    <option value="ADMIN_KEUANGAN">Admin Keuangan</option>
                    <option value="KASIR_LOKET">Kasir Loket</option>
                    <option value="KEPALA_SEKOLAH">Kepala Sekolah</option>
                    <option value="WALI_KELAS">Wali Kelas</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as any)}
                    className="w-full px-3 py-2 border rounded-lg bg-slate-50 focus:bg-white transition font-medium text-xs"
                  >
                    <option value="Aktif">Aktif</option>
                    <option value="Nonaktif">Nonaktif</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-2">Hak Akses Modul Khusus</label>
                <div className="grid grid-cols-2 gap-3 text-xs bg-slate-50 p-3 rounded-lg border">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={canTransaksi} onChange={(e) => setCanTransaksi(e.target.checked)} className="rounded text-sky-600 focus:ring-sky-500 w-4 h-4" />
                    <span>Transaksi Kasir</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={canVoid} onChange={(e) => setCanVoid(e.target.checked)} className="rounded text-rose-600 focus:ring-rose-500 w-4 h-4" />
                    <span>Void Kuitansi</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={canSetting} onChange={(e) => setCanSetting(e.target.checked)} className="rounded text-amber-600 focus:ring-amber-500 w-4 h-4" />
                    <span>Konfigurasi (Setting)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={canReport} onChange={(e) => setCanReport(e.target.checked)} className="rounded text-emerald-600 focus:ring-emerald-500 w-4 h-4" />
                    <span>Akses Laporan (Report)</span>
                  </label>
                </div>
              </div>

              <div className="pt-4 flex gap-3">
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 font-semibold transition disabled:opacity-50"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-lg font-semibold shadow-sm transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Menyimpan...</span>
                    </>
                  ) : (
                    <span>Simpan Data</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
